import { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  type RetrievePlatformConfigResult,
  type SupportedBusinessTypesGraphQLResponse
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import { supportedBusinessTypesQuery } from "../../Graphql/querries/platformConfigQuery";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

export const platformConfigQueryKey = [
  "platformConfig",
  "supportedBusinessTypes"
] as const;

export const useRetrievePlatformConfig = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery<RetrievePlatformConfigResult, Error>({
    queryKey: platformConfigQueryKey,
    staleTime: Infinity,
    retry: false,
    queryFn: async (): Promise<RetrievePlatformConfigResult> => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started retrieving platform config",
        buildLogContext("platform-config", "retrieve-platform-config")
      );

      const supportedResponse = await fetchGraphQL<
        SupportedBusinessTypesGraphQLResponse,
        Record<string, never>
      >(supportedBusinessTypesQuery, {}, setAlert);

      const { code, stage, businessTypes, featureFlags } =
        supportedResponse.supportedBusinessTypes;

      if (code !== API_RESPONSE_CODE.OK) {
        const error = new Error(
          `Retrieve platform config failed with code: ${code}`
        );
        captureAppException(
          "Retrieve platform config failed",
          error,
          buildLogContext(
            "platform-config",
            "retrieve-platform-config",
            undefined,
            undefined,
            undefined,
            { api_code: code }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: code as unknown as ALERT_BANNER_CODE
        });
        throw error;
      }

      const result: RetrievePlatformConfigResult = {
        stage: stage ?? null,
        businessTypes: businessTypes ?? [],
        featureFlags: featureFlags ?? {}
      };

      sendAppLog(
        LOG_LEVEL.INFO,
        "Platform config retrieved successfully",
        buildLogContext(
          "platform-config",
          "retrieve-platform-config",
          undefined,
          undefined,
          undefined,
          {
            stage: result.stage ?? "unknown",
            business_type_count: result.businessTypes.length
          }
        )
      );

      return result;
    }
  });
};
